import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { fieldCls, PasswordInput } from "@/components/FormBits";

export function DeleteAccount() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ password: "", confirm: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const ready = f.password && f.confirm.trim().toUpperCase() === "DELETE";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.post("/auth/delete-account", f);
      setUser(false);
      toast.success("Your account and its data have been deleted.");
      navigate("/welcome", { replace: true });
    } catch (err) {
      setError(errMsg(err));
      setBusy(false);
    }
  };

  return (
    <div data-testid="delete-account-section">
      <p className="text-sm leading-relaxed text-[#4A3B32]">
        Permanently delete your Ink Genius account. Your profile, kit, consultations and photos are removed immediately, remaining copies are deleted within 30 days, and backups expire within 90 days. This can't be undone.
      </p>
      <button onClick={() => { setF({ password: "", confirm: "" }); setError(""); setOpen(true); }} data-testid="delete-account-btn"
        className="mt-5 min-h-[48px] rounded-full border border-[#8C2A2A] px-6 text-sm font-semibold text-[#8C2A2A] transition-colors hover:bg-[#8C2A2A] hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#8C2A2A]">
        Delete account
      </button>
      <Dialog open={open} onOpenChange={(o) => !busy && setOpen(o)}>
        <DialogContent className="bg-[#FDFBF7]" data-testid="delete-account-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl">Delete your account?</DialogTitle>
            <DialogDescription className="text-[#6E5549]">This permanently removes your profile, kit, consultations and photos.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label htmlFor="delete-password-input" className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#6E5549]">Your password</label>
              <div className="mt-1.5"><PasswordInput id="delete-password-input" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} /></div>
            </div>
            <div>
              <label htmlFor="delete-confirm-input" className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#6E5549]">Type DELETE to confirm</label>
              <Input id="delete-confirm-input" data-testid="delete-confirm-input" autoComplete="off" value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} className={`mt-1.5 ${fieldCls}`} />
            </div>
            {error && <p role="alert" data-testid="delete-account-error" className="rounded-xl bg-[#F9ECEC] px-4 py-3 text-sm text-[#8C2A2A]">{error}</p>}
            <button type="submit" disabled={!ready || busy} data-testid="delete-account-confirm-btn"
              className="min-h-[48px] w-full rounded-full bg-[#8C2A2A] text-sm font-semibold text-white hover:bg-[#6f2121] disabled:cursor-not-allowed disabled:opacity-50">
              {busy ? "Deleting…" : "Permanently delete my account"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
